/**
 * Brush color state module — palette definition and active color management.
 *
 * Provides the 10-color Architectural Survey palette (option-a), active color
 * state, and helper functions for rendering fills and contrast-adaptive text.
 *
 * All fills are rendered at 0.35 opacity (rgba alpha 0.35).
 */

// ---------------------------------------------------------------------------
// Palette (Architectural Survey — muted, warm-cool balanced)
// ---------------------------------------------------------------------------

const PALETTE = [
  { name: "White",    hex: "#ffffff" },
  { name: "Sage",     hex: "#a8c5a0" },
  { name: "Sky",      hex: "#a0b8d8" },
  { name: "Rose",     hex: "#d4a8a0" },
  { name: "Wheat",    hex: "#d4c48a" },
  { name: "Lavender", hex: "#b8a8d4" },
  { name: "Mint",     hex: "#a0c8b8" },
  { name: "Sand",     hex: "#d4b890" },
  { name: "Blush",    hex: "#c4a8b8" },
  { name: "Slate",    hex: "#a8b4c4" },
];

// ---------------------------------------------------------------------------
// Module state
// ---------------------------------------------------------------------------

/** @type {string} */
let activeName = "White";

/** @type {string} */
let activeHex = "#ffffff";

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------

/**
 * Return the currently active brush color.
 *
 * @returns {{ name: string, hex: string }}
 */
export function getBrushColor() {
  return { name: activeName, hex: activeHex };
}

/**
 * Set the active brush color.
 *
 * @param {string} name - Human-readable color name
 * @param {string} hex  - Hex color string e.g. "#a8c5a0"
 */
export function setBrushColor(name, hex) {
  activeName = name;
  activeHex = hex;
}

/**
 * Convert a hex color string to an rgba fill style at 0.35 opacity.
 *
 * @param {string} hex - Hex color e.g. "#a8c5a0"
 * @returns {string}   - rgba string e.g. "rgba(168,197,160,0.35)"
 */
export function hexToFillStyle(hex) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},0.35)`;
}

/**
 * Return the active brush color as an rgba fill style at 0.35 opacity.
 *
 * @returns {string} - rgba string e.g. "rgba(255,255,255,0.35)"
 */
export function getBrushFillStyle() {
  return hexToFillStyle(activeHex);
}

/**
 * Return the palette array.
 *
 * @returns {Array<{ name: string, hex: string }>}
 */
export function getPalette() {
  return PALETTE;
}

/**
 * Return a contrast-appropriate text color for a given fill hex.
 *
 * Uses the W3C relative luminance formula: 0.299*r + 0.587*g + 0.114*b
 * where r, g, b are 0-1 floats. Returns dark text (#333333) for luminance
 * above 0.5, white text (#ffffff) for luminance below or equal to 0.5.
 *
 * @param {string} hex - Hex background color e.g. "#a8c5a0"
 * @returns {string}   - "#333333" or "#ffffff"
 */
export function contrastTextColor(hex) {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
  return luminance > 0.5 ? "#333333" : "#ffffff";
}
